import { HeartHandshake, Phone } from "lucide-react";
import { useNotify, type Identifier } from "ra-core";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import { PRODUCTS, labelOf } from "../gulaConstants";
import {
  callsFor,
  clientName,
  installedLabel,
  isDue,
  maintenanceSummary,
  sellsPlan,
  type WelcomeCall,
} from "./welcome";
import { PlanChoice } from "./PlanChoice";
import { WelcomeCallDialog } from "./WelcomeCallDialog";
import { takeWelcomeCall, usePendingWelcomeCalls } from "./useWelcomeCalls";

const PLAN_TONE = {
  sell: "bg-neutral-900 text-lime-300 dark:bg-lime-300 dark:text-neutral-900",
  ok: "bg-white/70 text-neutral-700 dark:bg-white/10 dark:text-neutral-200",
  warn: "bg-red-100 text-red-900 dark:bg-red-900/40 dark:text-red-100",
} as const;

const dueLabel = (call: WelcomeCall, now: Date) => {
  if (isDue(call, now)) return "Para hoy";
  return `El ${new Date(call.due_at).toLocaleDateString("es-ES", {
    weekday: "short",
    day: "numeric",
  })}`;
};

/**
 * The welcome calls to make: the day after each installation, the client gets
 * a call to see how it went and to hear about the maintenance plan.
 */
export const WelcomeCallsCard = ({
  meId,
  isAdmin,
  onChanged,
}: {
  meId: Identifier | null | undefined;
  isAdmin: boolean;
  onChanged?: () => void;
}) => {
  const notify = useNotify();
  const { data = [], refetch } = usePendingWelcomeCalls();
  const [open, setOpen] = useState<WelcomeCall | null>(null);
  const now = new Date();
  const calls = callsFor(data, meId, isAdmin);
  const changed = () => {
    refetch();
    onChanged?.();
  };

  const start = async (call: WelcomeCall) => {
    try {
      if (call.assigned_to == null) await takeWelcomeCall(call.id);
      setOpen(call);
    } catch (error) {
      notify(error instanceof Error ? error.message : "No se ha podido", {
        type: "error",
      });
    }
  };

  if (!calls.length) return null;
  const dueNow = calls.filter((call) => isDue(call, now)).length;
  const toSell = calls.filter(sellsPlan).length;

  return (
    <section className="flex flex-col gap-3 rounded-3xl border p-4">
      <div className="flex items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-lg font-semibold">
          <HeartHandshake className="h-5 w-5" />
          Llamadas de bienvenida
        </h2>
        <span className="text-sm text-muted-foreground">
          {dueNow ? `${dueNow} para hoy` : `${calls.length} programadas`}
          {toSell ? ` · ${toSell} con el plan por vender` : ""}
        </span>
      </div>
      <p className="text-sm text-muted-foreground">
        {isAdmin
          ? "Clientes recién instalados: pregúntales qué tal fue todo y apunta lo que digan. La etiqueta dice lo que ya tienen en Stripe (tócala para cambiarla). Los setters solo ven las de «Con venta del plan»."
          : "Clientes recién instalados sin plan de mantenimiento: pregúntales qué tal fue todo y ofréceles el plan. +3 puntos la llamada, +5 y 5 € si lo contratan."}
      </p>
      <ul className="flex flex-col gap-1.5">
        {calls.map((call) => {
          const due = isDue(call, now);
          const plan = maintenanceSummary(call);
          return (
            <li
              key={call.id}
              className={cn(
                "flex items-center gap-3 rounded-xl px-3 py-2 text-sm",
                due ? "bg-lime-100 dark:bg-lime-300/15" : "bg-muted/50",
              )}
            >
              <div className="flex min-w-0 flex-1 flex-col">
                <span className="truncate font-medium">
                  {clientName(call)}
                  {call.city ? (
                    <span className="font-normal text-muted-foreground">
                      {" "}
                      · {call.city}
                    </span>
                  ) : null}
                </span>
                <span className="truncate text-xs text-muted-foreground">
                  {[
                    call.product ? labelOf(PRODUCTS, call.product) : null,
                    installedLabel(call),
                    call.attempts ? `${call.attempts}º intento` : null,
                    isAdmin && call.assigned_name
                      ? `para ${call.assigned_name.split(" ")[0]}`
                      : null,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </span>
                {isAdmin ? (
                  <PlanChoice call={call} onChanged={changed}>
                    <span
                      className={cn(
                        "mt-1 w-fit rounded-full px-2 py-0.5 text-[11px] font-medium",
                        PLAN_TONE[plan.tone],
                      )}
                    >
                      {plan.chip}
                      {call.plan_override ? " · decidido por admin" : ""} ▾
                    </span>
                  </PlanChoice>
                ) : (
                  <span
                    className={cn(
                      "mt-1 w-fit rounded-full px-2 py-0.5 text-[11px] font-medium",
                      PLAN_TONE[plan.tone],
                    )}
                  >
                    {plan.chip}
                  </span>
                )}
              </div>
              <span className="shrink-0 text-xs text-muted-foreground">
                {dueLabel(call, now)}
              </span>
              <Button
                size="sm"
                variant={due ? "default" : "outline"}
                onClick={() => start(call)}
              >
                <Phone className="h-4 w-4" />
                Llamar
              </Button>
            </li>
          );
        })}
      </ul>
      <WelcomeCallDialog
        call={open}
        onClose={() => setOpen(null)}
        onChanged={changed}
      />
    </section>
  );
};
